import range from 'lodash/range';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import withResponsive from 'components/Layout/withResponsive';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import { clone } from 'lodash';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';

import { PAGE_SIZE } from 'constants/config';


class PaginationComponent extends PureComponent {
  static propTypes = {
    items: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
      page: PropTypes.number,
    }).isRequired,
    className: PropTypes.string,
    onChangePage: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    parentObjId: PropTypes.number,
    params: PropTypes.shape({
      search: PropTypes.string,
      status: PropTypes.string,
      statuses: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.string,
      ]),
      page: PropTypes.number,
    }),
    isMobile: PropTypes.bool.isRequired,
    isMedium: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    parentObjId: null,
    className: '',
    pageSize: PAGE_SIZE,
    params: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      pager: { pages: [] },
    };
  }

  componentDidMount() {
    // load initial pages
    if (this.props.items.results) {
      const currentPage = this.getCurrentPage();
      this.setPage(currentPage, true);
    }
  }

  componentDidUpdate(prevProps) {
    const { isMobile, isMedium, items } = this.props;
    // reset page if items array has changed
    if (JSON.stringify(items) !== JSON.stringify(prevProps.items)) {
      const pageInItem = clone(items.page);
      const currentPage = pageInItem || this.getCurrentPage();
      this.setPage(currentPage, true);
    }
    if (isMobile !== prevProps.isMobile || isMedium !== prevProps.isMedium) {
      const pageInItem = clone(items.page);
      const currentPage = pageInItem || this.getCurrentPage();
      this.setPage(currentPage, true);
    }
  }

  setPage = (page, ignore = false) => {
    const { items, parentObjId } = this.props;

    // get new pager object for specified page
    const pager = this.getPager(items.count, page);

    // update state
    this.setState({ pager });

    // call redux method to query specific page from backend
    // const isPagerSet = this.state.pager.startPage;
    if (!ignore) {
      // we don't need to fetch again if are on 1 page
      // because Django will by default get us 1 page
      if (parentObjId) {
        this.props.onChangePage(parentObjId, { ...this.props.params, page });
      } else {
        this.props.onChangePage({ ...this.props.params, page });
      }
    }
  };

  getPager = (totalItems, currentPage = 1) => {
    const { pageSize, isMobile, isMedium } = this.props;

    // calculate total pages
    const count = this.props.items.count ? this.props.items.count : 0;
    const totalPages = Math.ceil(count / pageSize);
    let maxAllowedPages = 10;
    if (isMobile) maxAllowedPages = 3;
    if (isMedium) maxAllowedPages = 5;

    let startPage;
    let endPage;

    if (totalPages <= maxAllowedPages) {
      // less than maxAllowedPages total pages so show all
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage + Math.floor(maxAllowedPages / 2) > totalPages) {
      startPage = Math.min(currentPage - Math.floor(maxAllowedPages / 2), totalPages - (maxAllowedPages - 1));
      endPage = Math.min(currentPage + Math.floor(maxAllowedPages / 2), totalPages);
    } else {
      startPage = Math.max(currentPage - Math.floor(maxAllowedPages / 2), 1);
      endPage = Math.max(currentPage + Math.floor(maxAllowedPages / 2), maxAllowedPages);
    }

    // create an array of pages to .map in the pager control
    const pages = range(startPage, endPage + 1);

    // return object with all pager properties required by the view
    return {
      totalItems,
      currentPage,
      pageSize,
      totalPages,
      startPage,
      endPage,
      pages,
    };
  };

  getCurrentPage = () => {
    let currentPage;
    const previousPage = this.props.items.previous;
    const nextPage = this.props.items.next;
    if (nextPage && !previousPage) {
      // it means that currentPage will be 1
      currentPage = 1;
    } else if (nextPage) {
      const nextPageParam = new URL(nextPage).searchParams.get('page');
      currentPage = parseInt(nextPageParam, 10) - 1;
    } else if (previousPage) {
      // it means that we've reached the end
      // if we don't have `page` param it means that we have only 2 pages
      const prevPageParam = new URL(previousPage).searchParams.get('page') || 1;
      currentPage = parseInt(prevPageParam, 10) + 1;
    } else {
      currentPage = 1;
    }
    return currentPage;
  }

  render() {
    const { className } = this.props;
    const { pager } = this.state;

    if (!(pager.pages && pager.pages.length > 1)) {
      // hide pagination if we don't have data
      return null;
    }

    return (
      <div className={`pagination__wrap ${className}`}>
        <Pagination className="pagination">
          <PaginationItem className="pagination__item" disabled={pager.currentPage === 1}>
            <PaginationLink
              className="pagination__link pagination__link--arrow"
              type="button"
              disabled={pager.currentPage === 1}
              onClick={() => this.setPage(1)}
            >
              <ChevronLeftIcon className="pagination__link-icon" />
            </PaginationLink>
          </PaginationItem>
          {pager.pages.map((page, index) =>
              (
                <PaginationItem
                  className="pagination__item"
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  disabled={pager.currentPage === page}
                  active={pager.currentPage === page}
                >
                  <PaginationLink
                    className="pagination__link"
                    type="button"
                    onClick={() => this.setPage(page)}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))
          }
          <PaginationItem className="pagination__item" disabled={pager.currentPage === pager.totalPages}>
            <PaginationLink
              className="pagination__link pagination__link--arrow"
              type="button"
              onClick={() => this.setPage(pager.totalPages)}
              disabled={pager.currentPage === pager.totalPages}
            >
              <ChevronRightIcon className="pagination__link-icon" />
            </PaginationLink>
          </PaginationItem>
        </Pagination>
      </div>
    );
  }
}

export default withResponsive(PaginationComponent);
export { PaginationComponent };
